import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { confirmSignOutWithUnsent, safeReturnTarget } from '../config/authRoutes';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../components/new-components/ui/button';
import { SecuritiesDisclaimer } from '../components/Auth/SecuritiesDisclaimer';
import { useUser } from '../contexts/UserContext';
import { BRAND_ASSETS, TENANT } from '../config';
import apiDefaults, { authApi } from '../services/api';

const RESEND_COOLDOWN_SECONDS = 30;

export default function VerifyEmailRequired() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useUser();

  /**
   * Where this person was going before verification interrupted them.
   *
   * `ProtectedRoute` has always passed it (`state: { from: location }`) and this
   * page never read it, so everyone landed on the investor properties page —
   * including a calling assistant who followed the Dialer's own bookmark and
   * was told "this page always opens the Dialer". Validated, never trusted.
   */
  const returnTo = safeReturnTarget((location.state as { from?: { pathname?: string; search?: string } } | null)?.from)
    ?? '/portal/invest/properties';
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);
  const [notVerifiedYet, setNotVerifiedYet] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Resend cooldown countdown — prevents mashing and reads as considered.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // When the cooldown expires after a successful send, return to the idle state so the
  // "Resend it" button comes back — otherwise the resend affordance is unreachable after
  // the first send (the button only renders in the idle branch).
  useEffect(() => {
    if (cooldown === 0 && resendStatus === 'success') setResendStatus('idle');
  }, [cooldown, resendStatus]);

  const handleResendEmail = async () => {
    setResending(true);
    setResendStatus('idle');
    setNotVerifiedYet(false);
    try {
      const email = user?.email;
      if (!email) {
        setResendStatus('error');
        return;
      }

      const response = await apiDefaults.post('/auth/resend-verification', { email }) as any;
      if (response?.success) {
        // If backend says already verified, refresh user state and head into the portal
        if (response.message === 'Email already verified') {
          await refreshUser();
          navigate(returnTo);
          return;
        }
        setResendStatus('success');
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setResendStatus('error');
      }
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setResendStatus('error');
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    setNotVerifiedYet(false);
    setResendStatus('idle');
    try {
      // Fetch fresh data directly — `user` from the closure is stale until React re-renders.
      const fresh = await authApi.getProfile();
      const freshData = fresh.data as any;
      if (fresh.success && (freshData?.emailVerified || freshData?.email_verified)) {
        await refreshUser(); // sync global state
        navigate(returnTo);
      } else {
        // Give explicit feedback instead of silently doing nothing.
        setNotVerifiedYet(true);
      }
    } catch (error) {
      console.error('Failed to check verification status:', error);
      setNotVerifiedYet(true);
    } finally {
      setChecking(false);
    }
  };

  // "Use a different email" = abandon this unverified account and start signup over.
  const handleUseDifferentEmail = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col portal-typography">
      {/* Header — centered logo. No back button: there's no sensible "back" from a
          just-created account (the escape hatch is "Use a different email" below). */}
      <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 flex justify-center">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <img src={BRAND_ASSETS.logo.primary} alt={TENANT.branding.name} className="h-11 object-contain" />
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="tab-panel-in max-w-xl w-full text-center">
          <div className="space-y-2">
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance] focus-visible:outline-none"
            >
              Welcome to {TENANT.branding.name}
            </h1>
            <p className="text-lg text-foreground [text-wrap:balance]">
              Your account is created. Verify your email to continue.
            </p>
            <p className="text-sm text-muted-foreground">
              We sent a verification link to{' '}
              <span className="font-medium text-foreground">{user?.email}</span>.
              <br />
              Don't see it? Check your spam folder.
            </p>
          </div>

          {/* Primary action */}
          <div className="mt-8 max-w-md mx-auto">
            <Button
              onClick={handleCheckVerification}
              disabled={checking}
              className="w-full h-14 text-lg"
            >
              {checking ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Checking…
                </>
              ) : (
                "I've verified my email"
              )}
            </Button>
            {notVerifiedYet && (
              <p className="mt-4 text-sm text-destructive">
                Not verified yet. Click the link in your email, then try again.
              </p>
            )}
          </div>

          {/* Secondary: resend, with a cooldown */}
          <div className="mt-5 text-sm text-muted-foreground">
            {resendStatus === 'success' ? (
              <span className="text-foreground">
                {cooldown > 0
                  ? `Sent. You can resend in ${cooldown}s.`
                  : 'Verification email sent. Check your inbox.'}
              </span>
            ) : resendStatus === 'error' ? (
              <span className="inline-flex items-center gap-1.5 text-destructive">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                Couldn't send. Please try again.
              </span>
            ) : (
              <>
                Didn't get the email?{' '}
                <button
                  onClick={handleResendEmail}
                  disabled={resending || cooldown > 0}
                  className="font-semibold text-foreground underline disabled:no-underline disabled:text-muted-foreground disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend it'}
                </button>
              </>
            )}
          </div>

          {/* Tertiary — quiet escape hatch */}
          <button
            onClick={handleUseDifferentEmail}
            className="mt-8 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Use a different email
          </button>
        </div>
      </div>

      {/* Footer */}
      <SecuritiesDisclaimer className="pb-6 px-4" />
    </div>
  );
}
